import { formatUserRoleType } from "@utils/data-display";
import { hasUserValidPermissionProjectLevel } from "@utils/permission-control/project-permission-control";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { getPrettyName } from "@utils/user-utils";

/** Checks if the current user is allowed to change the project manager and group */
export function isAllowedToChangeManagement(isDemoProject, selectedProject) {
  if (isDemoProject) {
    return false;
  }
  return hasUserValidPermissionProjectLevel({
    selectedProject,
    permissionName: RequiredPermissionProjectLevelName.canChangeProjectGroup
  });
}

/** The roles in a group that let a member manage its projects, in the order they are offered */
export const GROUP_MANAGING_ROLES = ["COMPANY_MANAGER", "PROJECT_MANAGER"];

/**
 * The role a group viewer chosen as the project manager gets in the group. Group manager, rather
 * than project manager, so that they keep seeing all the projects of the group.
 */
export const GROUP_VIEWER_PROMOTION_ROLE = "COMPANY_MANAGER";

/**
 * What has to change about the membership of the new project manager in the group of the project:
 * nothing, joining the group, or changing their role in it from group viewer.
 */

/** Whether a role in a group lets the member manage the projects of the group */
export function isGroupManagingRole(role) {
  return GROUP_MANAGING_ROLES.some(managingRole => managingRole === role);
}

/**
 * Whether two entries are the same member. A member who did not accept their invite yet can be
 * listed with another identity by the workspace than by the group, while the email is the same.
 */
function isSameMember(member, other) {
  return member.identity === other.identity || !!member.email && member.email.toLowerCase() === other.email?.toLowerCase();
}

/**
 * Returns the entry of the member in the group, or undefined when they do not belong to it
 *
 * @param member The member to look up
 * @param groupMembers The members of the group
 */
export function findGroupMember(member, groupMembers) {
  return groupMembers.find(groupMember => isSameMember(groupMember, member));
}

/**
 * Returns the members of the group who can manage its projects: its group and project managers.
 *
 * @param groupMembers The members of the group
 */
export function getGroupManagers(groupMembers) {
  return groupMembers.filter(({
    role
  }) => isGroupManagingRole(role));
}

/**
 * Returns what tells the candidates apart when choosing the project manager, by identity: their
 * role in the selected group, whether they accepted their invite or not. Candidates who do not
 * belong to the group get no note.
 *
 * @param candidates The members who can be chosen as the project manager
 * @param groupMembers The members of the selected group
 */
export function getManagerNotes(candidates, groupMembers) {
  const notes = new Map();
  for (const candidate of candidates) {
    const groupRole = findGroupMember(candidate, groupMembers)?.role;
    if (groupRole) {
      notes.set(candidate.identity, formatUserRoleType(groupRole));
    }
  }
  return notes;
}

/**
 * Returns what has to change about the membership of the new project manager in the group of the
 * project. A new member invited by email has to join it, and so does a member of the workspace who
 * does not belong to it, admins included, while a group viewer has to become a manager in it.
 *
 * @param selectedMember The new project manager, or the email of the member to invite
 * @param groupMembers The members of the selected group
 */
export function getGroupMembershipChange(selectedMember, groupMembers) {
  if (!selectedMember) {
    return "none";
  }
  if (typeof selectedMember === "string") {
    return "join";
  }
  const groupMember = findGroupMember(selectedMember, groupMembers);
  if (!groupMember) {
    return "join";
  }
  return isGroupManagingRole(groupMember.role) ? "none" : "changeRole";
}

/** Returns the key of a member managing projects in a group, to remember who was added to which */
export function getGroupManagerKey(groupId, identity) {
  return `${groupId}:${identity}`;
}

/**
 * Sorts the candidates for the project manager ascending by the name the list shows, which is
 * their email when they have no name, such as a member who did not accept their invite yet
 *
 * @param candidates The members who can be chosen as the project manager
 * @returns A new sorted array of the candidates
 */
export function sortCandidatesByName(candidates) {
  return [...candidates].sort((a, b) => getPrettyName(a).localeCompare(getPrettyName(b), undefined, {
    sensitivity: "base"
  }));
}